/**
 * 真人出镜「身份参考」三态(2026-09-20,用户反馈:「选择的时候现在好像只能选照片,不能单选视频」)。
 *
 * ═══ 为什么从布尔升成档位 ═══════════════════════════════════════════════════════
 * 0915 归一批 B 把这件事做成了布尔 `identityUseVideo`,只能表达「只用定妆照」与
 * 「定妆照 ＋ 身份视频」两档 —— 用户真正想要的第三种意图「这一单就照着我这段视频来,
 * 别拿照片」表达不出来:布尔的两个值都带着照片。再加第二个布尔就是 4 种组合,其中
 * 「不要图也不要视频」根本不成立;用档位枚举,非法组合压根表达不出来。
 *
 * ═══ 与 api 的契约 ═════════════════════════════════════════════════════════════
 * 提交时发新字段 `identityRefMode`(逐字 photo | photo_video | video),同时发旧布尔
 * `identityUseVideo` 作**兼容镜像**(= 这一档带不带视频)。api 侧 `IdentityRefMode.resolve`
 * 两者都给时以新字段为准;镜像存在的意义是:万一 console 先上线、api 还是旧版,旧版读到
 * 镜像至少还是「带视频」这一族,不会把用户选的视频档静默读成只用照片。
 *
 * ═══ 计费口径不变 ═══════════════════════════════════════════════════════════════
 * 带视频(含仅视频)= vin 变体,qty = 出片秒 + 身份视频秒。单价与计量公式一个字没改,
 * 本模块只回答「送哪些参考」以及「这一档算不算 vin」({@link identityRefBillsAsVideoInput}
 * 是这条口径在 console 侧的唯一谓词,报价与 UI 都读它,不各判一遍)。
 */

/** 逐字对齐 api `IdentityRefMode.wireValue()` / request_payload 里的串。 */
export type IdentityRefMode = "photo" | "photo_video" | "video";

/**
 * 缺省档 —— 老板 0915 的判断未变:一张定妆照通常就够,**不默认多收钱**
 * (vin 与 novin 单价完全一样,差别只在计量:身份视频封顶 15s 是固定开销,摊不薄)。
 */
export const DEFAULT_IDENTITY_REF_MODE: IdentityRefMode = "photo";

/** 计入「有没有定妆照」的 slot 家族 —— 与 api `CharacterOwnRefs.IMAGE_SLOTS` 逐字同源。 */
const IMAGE_SLOTS = ["identity", "expression", "pose"];

/** 本模块要读的人物字段(只取需要的那几个,不把整个 CharacterDto 拖进纯函数层)。 */
export interface IdentityRefCharacter {
  hasVideoAvatar?: boolean;
  identityVideoDurationSec?: number | null;
  primaryKind?: "image" | "video" | null;
  materials?: readonly { slot?: string | null; kind?: "image" | "video" | null }[] | null;
}

/** 这一档按 vin(带视频输入)计费吗。报价与「多花多少积分」提示唯一读它。 */
export function identityRefBillsAsVideoInput(mode: IdentityRefMode): boolean {
  return mode !== "photo";
}

/**
 * 这个人名下有没有能当定妆照用的图素材。
 *
 * ★口径与 api 选材端一致:只认 identity/expression/pose 三个图槽,`kind` 缺席的存量行按图处理
 * (归一前这张表恒图)。造型/场景槽里的图不进出片参考集,不能拿来当"有定妆照"的证据。
 *
 * ★`materials` 只有**详情**端点带;列表 DTO 没有这张表。拿不到时**保守返回 true**
 * (维持缺省定妆照档),不凭一个缺席字段去判定一个人"没有照片"——这条与
 * `hasVideoAvatar` 缺席按 false 处理是同一条向后兼容纪律:不把"不知道"升级成"确定"。
 */
export function hasIdentityPhoto(c: IdentityRefCharacter): boolean {
  const materials = c.materials;
  if (materials == null) return true;
  return materials.some(
    (m) => IMAGE_SLOTS.includes(m.slot ?? "") && (m.kind ?? "image") !== "video"
  );
}

/**
 * 选中这个人物时单选组缺省落哪一档。
 *
 * ★唯一会偏离缺省的情形:这个人**只有身份视频、没有任何定妆照**(归一批 B 之后这是允许存在的
 * 形态 —— 人物详情页可以删掉照片素材、虚拟人建档可以只传视频)。此时落在定妆照档上是个
 * **实际不可用**的缺省:提交必被 api 的素材就绪守卫拒掉,而用户看不出为什么。
 */
export function defaultIdentityRefMode(c: IdentityRefCharacter): IdentityRefMode {
  if (c.hasVideoAvatar === true && !hasIdentityPhoto(c)) return "video";
  return DEFAULT_IDENTITY_REF_MODE;
}

export interface IdentityRefOption {
  value: IdentityRefMode;
  /** chip 上的文案(带视频的两档在有实测秒时把秒数写进去,没有就不编一个数)。 */
  label: string;
  /** 这一档按 vin 计费 —— 加价提示挂在它上面。 */
  billsAsVideoInput: boolean;
  disabled: boolean;
  /** 置灰原因(disabled 时必给,直接显示给用户,不让人对着灰按钮猜)。 */
  disabledReason?: string;
}

const NO_PHOTO_REASON = "这位出镜人还没有定妆照,先去 ta 的档案里传一张";

/**
 * 三档单选组。**人物没有身份视频时返回空数组** —— 只能选一档的单选组是纯噪声,
 * 调用方据此整组不渲染(与 0915 的判据一致,不是新规矩)。
 */
export function identityRefOptions(c: IdentityRefCharacter): IdentityRefOption[] {
  if (c.hasVideoAvatar !== true) return [];
  const sec = c.identityVideoDurationSec;
  const suffix = sec ? ` ${sec}s` : "";
  const photo = hasIdentityPhoto(c);
  const noPhoto = photo ? undefined : NO_PHOTO_REASON;
  return [
    {
      value: "photo",
      label: "定妆照",
      billsAsVideoInput: false,
      disabled: !photo,
      disabledReason: noPhoto,
    },
    {
      value: "photo_video",
      label: `定妆照 ＋ 身份视频${suffix}`,
      billsAsVideoInput: true,
      disabled: !photo,
      disabledReason: noPhoto,
    },
    {
      value: "video",
      label: `仅身份视频${suffix}`,
      billsAsVideoInput: true,
      disabled: false,
    },
  ];
}
